import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  ClipboardCheck,
  Shield,
  Camera,
  Upload,
  X,
  ChevronDown,
  FileText as FilePdf
} from 'lucide-react';

const HSEAssessmentForm = ({
  assessmentForm,
  assessmentRequirements,
  setAssessmentRequirements,
  handleFormChange,
  fieldImages,
  setFieldImages,
  uploadedImages,
  setUploadedImages,
  currentStep,
  setCurrentStep,
  nextStep,
  prevStep,
  validateCurrentStep,
  submitAssessment,
  isLoading,
  error,
  success,
  properties,
  users,
  selectedAssessment,
  setActiveView,
  isGeneratingPDF,
  handleDownloadPDF,
  user,
  resetForm
}) => {
  // Camera-related state and refs (own by form)
  const [cameraActive, setCameraActive] = useState(false);
  const [currentCameraField, setCurrentCameraField] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Collapsed sections state (owned by form)
  const [collapsedSections, setCollapsedSections] = useState({
    fire_equipment: true,
    sprinkler_system: true,
    emergency_preparedness: true
  });

  // Camera cleanup effect
  useEffect(() => {
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);

  // Camera functions with fallback logic
  const startCamera = async (fieldKey = null) => {
    try {
      console.log('Starting camera for field:', fieldKey);

      // First, set the camera state to render the video element
      setCameraActive(true);
      setCurrentCameraField(fieldKey);

      // Wait for the next tick to ensure video element is rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      // Try back camera first (environment)
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'environment' // Back camera for better quality
          }
        });
        console.log('Back camera stream obtained');
      } catch (backCameraError) {
        console.log('Back camera not available, trying front camera...');
        // Fallback to front camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user' // Front camera
          }
        });
        console.log('Front camera stream obtained');
      }

      // Retry mechanism for video ref
      let retryCount = 0;
      const maxRetries = 10;

      const assignStream = () => {
        if (videoRef.current && stream) {
          console.log('Assigning stream to video element');

          const video = videoRef.current;

          const handleLoadedMetadata = () => {
            console.log('Video metadata loaded, camera ready');
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          };

          const handleError = (e) => {
            console.error('Video error:', e);
            video.removeEventListener('error', handleError);
          };

          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          video.addEventListener('error', handleError);

          // Assign stream to video element
          video.srcObject = stream;
          video.muted = true; // Required for autoplay in some browsers

        } else if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Video ref not ready, retrying... (${retryCount}/${maxRetries})`);
          setTimeout(assignStream, 50);
        } else {
          console.error('Video ref not available after retries, stopping camera');
          // Stop the stream if we can't assign it
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
          setCameraActive(false);
          setCurrentCameraField(null);
          alert('Unable to initialize camera. Please try again or use file upload instead.');
        }
      };

      assignStream();

    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraActive(false);
      setCurrentCameraField(null);
      alert('Unable to access camera. Please check permissions and ensure your device has a camera, or use file upload instead.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
      setCurrentCameraField(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);

      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);

      // Add captured photo to appropriate image collection
      if (currentCameraField) {
        // Add to field-specific images
        const fieldImageKey = `${currentCameraField}_images`;
        setFieldImages(prev => ({
          ...prev,
          [fieldImageKey]: [...(prev[fieldImageKey] || []), {
            id: `temp-${currentCameraField}-${Date.now()}-${Math.random()}`,
            filename: `camera-capture-${Date.now()}.jpg`,
            content_type: 'image/jpeg',
            size: photoDataUrl.length,
            data: photoDataUrl,
            temporary: true,
            field: currentCameraField
          }]
        }));
      } else {
        // Add to general images
        setUploadedImages(prev => [...prev, {
          id: `temp-${Date.now()}-${Math.random()}`,
          filename: `camera-capture-${Date.now()}.jpg`,
          content_type: 'image/jpeg',
          size: photoDataUrl.length,
          data: photoDataUrl,
          temporary: true
        }]);
      }

      stopCamera();
    }
  };

  const renderComplianceField = (field, label, category) => {
    const complianceField = field + '_compliance';
    const commentsField = field + '_comments';

    return (
      <div key={field} className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">{label}</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compliance Status
            </label>
            <select
              value={assessmentForm[complianceField]}
              onChange={(e) => handleFormChange(complianceField, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="not_applicable">Not Applicable</option>
              <option value="compliant">Compliant</option>
              <option value="partial">Partially Compliant</option>
              <option value="non_compliant">Non-Compliant</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comments
            </label>
            <textarea
              value={assessmentForm[commentsField]}
              onChange={(e) => handleFormChange(commentsField, e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add comments or observations..."
            />
          </div>
        </div>
      </div>
    );
  };

  // Multi-step wizard render functions
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-4 mb-6">
      <div className={`flex items-center space-x-2 ${
        currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'
      }`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
        }`}>
          1
        </div>
        <span className="font-medium">Basic Information</span>
      </div>

      <div className={`w-12 h-0.5 ${
        currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'
      }`} />

      <div className={`flex items-center space-x-2 ${
        currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'
      }`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
        }`}>
          2
        </div>
        <span className="font-medium">Assessment Information</span>
      </div>
    </div>
  );

  const renderAssessmentRequirements = () => (
    <div>
      <h4 className="text-base sm:text-md font-medium text-gray-900 mb-4">Assessment Requirements</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { key: 'legal_wall_charts', label: 'Legal wall charts displayed' },
          { key: 'signage_displayed', label: 'Signage displayed' },
          { key: 'electrical_db', label: 'Electrical DB' },
          { key: 'fire_fighting_equipment', label: 'Fire Fighting Equipment' },
          { key: 'fire_detection_systems', label: 'Fire detection and alarm systems' },
          { key: 'stacking_articles', label: 'Stacking of Articles' },
          { key: 'automatic_sprinkler', label: 'Automatic Sprinkler System' },
          { key: 'evacuation_routes', label: 'Evacuation Routes & Exits' },
          { key: 'housekeeping', label: 'Housekeeping' },
          { key: 'emergency_preparedness', label: 'Emergency Preparedness' },
          { key: 'hazardous_chemicals', label: 'Hazardous Chemical Agents (HCA)' },
          { key: 'general_condition', label: 'General Condition' }
        ].map(item => (
          <label key={item.key} className="flex items-start space-x-2 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={assessmentRequirements[item.key]}
              onChange={(e) => setAssessmentRequirements(prev => ({
                ...prev,
                [item.key]: e.target.checked
              }))}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 mt-0.5"
            />
            <span className="text-sm text-gray-700 leading-tight">{item.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderComplianceRadio = (field, label) => {
    const complianceField = field + '_compliance';
    const commentsField = field + '_comments';

    return (
      <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          <h5 className="font-medium text-gray-900 text-sm sm:text-base">{label}</h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
            {[
              { value: 'compliant', label: 'Compliant', color: 'text-green-600' },
              { value: 'non_compliant', label: 'Non-Compliant', color: 'text-red-600' },
              { value: 'partial', label: 'Partially Compliant', color: 'text-yellow-600' },
              { value: 'not_applicable', label: 'Not Applicable', color: 'text-gray-600' }
            ].map(option => (
              <label key={option.value} className="flex items-center space-x-3 p-3 sm:p-2 rounded-md hover:bg-gray-50 cursor-pointer min-h-[48px] sm:min-h-[44px] border border-transparent hover:border-gray-200 transition-colors">
                <input
                  type="radio"
                  name={complianceField}
                  value={option.value}
                  checked={assessmentForm[complianceField] === option.value}
                  onChange={(e) => handleFormChange(complianceField, e.target.value)}
                  className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 border-gray-300 focus:ring-blue-500 flex-shrink-0"
                />
                <span className={`text-sm sm:text-xs lg:text-sm font-medium ${option.color} leading-tight flex-1`}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <input
            type="text"
            placeholder="Comments"
            value={assessmentForm[commentsField] || ''}
            onChange={(e) => handleFormChange(commentsField, e.target.value)}
            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] sm:min-h-0"
          />
        </div>

        {/* Individual image upload for this field */}
        {renderFieldImageUpload(field, label)}
      </div>
    );
  };

  const renderCollapsibleSection = (sectionKey, title, content) => {
    const isCollapsed = collapsedSections[sectionKey];

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
          }))}
          className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[60px] sm:min-h-[56px]"
        >
          <span className="font-medium text-gray-900 text-base sm:text-sm pr-4">{title}</span>
          <div className="flex-shrink-0">
            <ChevronDown className={`w-6 h-6 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-200 ${
              isCollapsed ? '' : 'rotate-180'
            }`} />
          </div>
        </button>

        {!isCollapsed && (
          <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-gray-200 bg-gray-50">
            <div className="pt-4 sm:pt-5">
              {content}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Individual field image upload component
  const renderFieldImageUpload = (fieldKey, label) => {
    const fieldImageKey = `${fieldKey}_images`;
    const currentImages = fieldImages[fieldImageKey] || [];
    const isCurrentFieldCamera = cameraActive && currentCameraField === fieldKey;

    const handleFieldImageUpload = (event) => {
      const files = Array.from(event.target.files);

      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setFieldImages(prev => ({
              ...prev,
              [fieldImageKey]: [...(prev[fieldImageKey] || []), {
                id: `temp-${fieldKey}-${Date.now()}-${Math.random()}`,
                filename: file.name,
                content_type: file.type,
                size: file.size,
                data: e.target.result,
                temporary: true,
                field: fieldKey
              }]
            }));
          };
          reader.readAsDataURL(file);
        }
      });
    };

    const removeFieldImage = (imageIndex) => {
      setFieldImages(prev => ({
        ...prev,
        [fieldImageKey]: (prev[fieldImageKey] || []).filter((_, i) => i !== imageIndex)
      }));
    };

    const handleStartCamera = () => {
      if (cameraActive && currentCameraField !== fieldKey) {
        stopCamera();
      }
      startCamera(fieldKey);
    };

    return (
      <div className="mt-4">
        <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <span className="text-sm font-medium text-gray-700">Supporting Images</span>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
              <button
                type="button"
                onClick={handleStartCamera}
                className="inline-flex items-center justify-center px-4 py-3 sm:px-3 sm:py-2 bg-green-600 text-white text-sm sm:text-xs rounded-lg hover:bg-green-700 active:bg-green-800 min-h-[48px] sm:min-h-[36px] transition-colors"
              >
                <Camera className="w-4 h-4 sm:w-3 sm:h-3 mr-2 sm:mr-1" />
                {isCurrentFieldCamera ? 'Camera Active' : 'Take Photo'}
              </button>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFieldImageUpload}
                className="hidden"
                id={`field-image-upload-${fieldKey}`}
              />
              <label
                htmlFor={`field-image-upload-${fieldKey}`}
                className="inline-flex items-center justify-center px-4 py-3 sm:px-3 sm:py-2 bg-blue-600 text-white text-sm sm:text-xs rounded-lg hover:bg-blue-700 active:bg-blue-800 cursor-pointer min-h-[48px] sm:min-h-[36px] transition-colors"
              >
                <Upload className="w-4 h-4 sm:w-3 sm:h-3 mr-2 sm:mr-1" />
                Upload
              </label>
            </div>
          </div>

          {/* Camera preview for this field */}
          {isCurrentFieldCamera && (
            <div className="mb-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-48 sm:h-56 md:h-64 object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />
                {/* Camera overlay with viewfinder */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Viewfinder frame */}
                  <div className="absolute inset-4 sm:inset-6 border-2 border-white border-opacity-50 rounded-lg">
                    {/* Corner guides */}
                    <div className="absolute top-0 left-0 w-4 h-4 sm:w-6 sm:h-6 border-t-2 border-l-2 border-white"></div>
                    <div className="absolute top-0 right-0 w-4 h-4 sm:w-6 sm:h-6 border-t-2 border-r-2 border-white"></div>
                    <div className="absolute bottom-0 left-0 w-4 h-4 sm:w-6 sm:h-6 border-b-2 border-l-2 border-white"></div>
                    <div className="absolute bottom-0 right-0 w-4 h-4 sm:w-6 sm:h-6 border-b-2 border-r-2 border-white"></div>
                  </div>
                  {/* Center crosshair */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 border border-white border-opacity-75 rounded-full">
                      <div className="w-2 h-2 bg-white bg-opacity-75 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                    </div>
                  </div>
                </div>
                {/* Camera controls overlay */}
                <div className="absolute bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3 sm:gap-4">
                  <button
                    type="button"
                    onClick={capturePhoto}
                    className="w-16 h-16 sm:w-14 sm:h-14 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
                  >
                    <div className="w-10 h-10 sm:w-8 sm:h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <Camera className="w-5 h-5 sm:w-4 sm:h-4 text-white" />
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={stopCamera}
                    className="w-12 h-12 sm:w-10 sm:h-10 bg-red-600 bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 active:scale-95"
                  >
                    <X className="w-5 h-5 sm:w-4 sm:h-4 text-white" />
                  </button>
                </div>
              </div>
              <p className="text-sm sm:text-xs text-gray-600 text-center mt-2 sm:mt-1">Frame the subject and tap capture</p>
            </div>
          )}

          {currentImages.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-2">
              {currentImages.map((image, index) => (
                <div key={image.id || index} className="relative group">
                  <img
                    src={image.data || (image instanceof File ? URL.createObjectURL(image) : image)}
                    alt={`${label} ${index + 1}`}
                    className="w-full h-20 sm:h-16 object-cover rounded-lg border border-gray-200 transition-transform group-hover:scale-105"
                  />
                  <button
                    onClick={() => removeFieldImage(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 sm:w-5 sm:h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-sm sm:text-xs hover:bg-red-600 active:bg-red-700 shadow-lg transition-colors"
                  >
                    ×
                  </button>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-opacity"></div>
                </div>
              ))}
            </div>
          )}

          {currentImages.length === 0 && !isCurrentFieldCamera && (
            <div className="text-center py-4 sm:py-3">
              <div className="w-12 h-12 sm:w-10 sm:h-10 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                <Camera className="w-6 h-6 sm:w-5 sm:h-5 text-gray-400" />
              </div>
              <p className="text-sm sm:text-xs text-gray-500">
                No images uploaded for this item
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderImageUpload = () => {
    const isGeneralCamera = cameraActive && currentCameraField === null;

    const handleImageUpload = (event) => {
      const files = Array.from(event.target.files);

      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setUploadedImages(prev => [...prev, {
              id: `temp-${Date.now()}-${Math.random()}`,
              filename: file.name,
              content_type: file.type,
              size: file.size,
              data: e.target.result, // This is the base64 string
              temporary: true
            }]);
          };
          reader.readAsDataURL(file);
        }
      });
    };

    const handleStartGeneralCamera = () => {
      if (cameraActive && currentCameraField !== null) {
        stopCamera();
      }
      startCamera(null); // null indicates general images
    };

    return (
      <div>
        <h4 className="text-md font-medium text-gray-900 mb-4">General Images</h4>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <div className="flex justify-center gap-3 mb-4">
            <button
              type="button"
              onClick={handleStartGeneralCamera}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Camera className="w-4 h-4 mr-2" />
              {isGeneralCamera ? 'Camera Active' : 'Take Photo'}
            </button>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Photos
            </label>
          </div>

          {/* Camera preview for general images */}
          {isGeneralCamera && (
            <div className="mb-4">
              <div className="relative bg-black rounded-lg overflow-hidden max-w-md mx-auto">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-64 object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />
                {/* Camera overlay with viewfinder */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Viewfinder frame */}
                  <div className="absolute inset-6 border-2 border-white border-opacity-50 rounded-lg">
                    {/* Corner guides */}
                    <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-white"></div>
                    <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-white"></div>
                    <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-white"></div>
                    <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-white"></div>
                  </div>
                  {/* Center crosshair */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-6 h-6 border border-white border-opacity-75 rounded-full">
                      <div className="w-2 h-2 bg-white bg-opacity-75 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                    </div>
                  </div>
                </div>
                {/* Camera controls overlay */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3">
                  <button
                    type="button"
                    onClick={capturePhoto}
                    className="w-16 h-16 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 transform hover:scale-105"
                  >
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={stopCamera}
                    className="w-12 h-12 bg-red-600 bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 text-center mt-2">Position the subject within the frame and tap capture</p>
            </div>
          )}

          {!isGeneralCamera && (
            <p className="text-sm text-gray-500 mb-4">
              Take photos with your camera or upload existing images
            </p>
          )}

          {uploadedImages.length > 0 && (
            <>
              <p className="text-sm text-gray-600 mt-4">
                {uploadedImages.length} image(s) selected
              </p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                {uploadedImages.map((image, index) => (
                  <div key={image.id || index} className="relative">
                    <img
                      src={image.data || (image instanceof File ? URL.createObjectURL(image) : image)}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-20 object-cover rounded-md"
                    />
                    <button
                      onClick={() => setUploadedImages(prev => prev.filter((_, i) => i !== index))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3 sm:mb-4">
        <h3 className="text-lg sm:text-xl font-medium text-gray-900">Basic Information</h3>
        <p className="text-xs text-gray-500"><span className="text-red-500">*</span> Required fields</p>
      </div>

      {/* Property and Tenant Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Property/Location <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={assessmentForm.building_id}
            onChange={(e) => handleFormChange('building_id', e.target.value)}
            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] sm:min-h-0"
            placeholder="Enter property / location"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Tenant Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={assessmentForm.tenant_name}
            onChange={(e) => handleFormChange('tenant_name', e.target.value)}
            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] sm:min-h-0"
            placeholder="Enter tenant name"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Auditor Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={assessmentForm.auditor_name}
            onChange={(e) => handleFormChange('auditor_name', e.target.value)}
            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] sm:min-h-0"
            placeholder="Enter auditor name"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Assessment Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={assessmentForm.assessment_date}
            onChange={(e) => handleFormChange('assessment_date', e.target.value)}
            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] sm:min-h-0"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Employee Count
          </label>
          <input
            type="number"
            value={assessmentForm.employee_count}
            onChange={(e) => handleFormChange('employee_count', e.target.value)}
            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] sm:min-h-0"
            placeholder="Number of employees"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Tenant Representative
          </label>
          <input
            type="text"
            value={assessmentForm.tenant_representative}
            onChange={(e) => handleFormChange('tenant_representative', e.target.value)}
            className="w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-sm min-h-[44px] sm:min-h-0"
            placeholder="Name of tenant representative"
          />
        </div>
      </div>

      {renderAssessmentRequirements()}
    </div>
  );

  // Mapping between assessment requirements checkboxes and their corresponding sections
  const getSectionMapping = () => {
    const sections = [];

    // Legal Wall Charts - always show if legal_wall_charts is selected
    if (assessmentRequirements.legal_wall_charts) {
      sections.push({
        type: 'static',
        key: 'legal_wall_charts',
        title: 'Legal wall charts displayed',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('ohs_act', 'OHS Act 85 of 1993 & Regulations')}
            {renderComplianceRadio('labour_relations_act', 'Labour Relations Act 66 of 1995')}
            {renderComplianceRadio('basic_conditions_employment', 'Basic Conditions of Employment Act 75 of 1997')}
            {renderComplianceRadio('coida', 'COIDA')}
          </div>
        )
      });
    }

    // Fire Fighting Equipment
    if (assessmentRequirements.fire_fighting_equipment) {
      sections.push({
        type: 'collapsible',
        key: 'fire_equipment',
        title: 'Fire Fighting Equipment',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('fire_extinguishers', 'Fire Extinguishers')}
            {renderComplianceRadio('fire_hose_reels', 'Fire Hose Reels')}
            {renderComplianceRadio('fire_blankets', 'Fire Blankets')}
            {renderComplianceRadio('emergency_lighting', 'Emergency Lighting')}
            {renderComplianceRadio('fire_pump', 'Fire Pump')}
            {renderComplianceRadio('fire_hydrants', 'Fire Hydrants')}
            {renderComplianceRadio('fire_suppression_system', 'Fire Suppression System')}
            {renderComplianceRadio('fire_safety_signage', 'Fire Safety Signage')}
          </div>
        )
      });
    }

    // Automatic Sprinkler System
    if (assessmentRequirements.automatic_sprinkler) {
      sections.push({
        type: 'collapsible',
        key: 'sprinkler_system',
        title: 'Automatic Sprinkler System',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('sprinkler_system_serviced', 'Sprinkler system serviced')}
            {renderComplianceRadio('sprinkler_valves_locked_chain_padlock', 'Sprinkler valves locked with chain and padlock')}
            {renderComplianceRadio('sprinkler_valve_chamber_spare_parts', 'Sprinkler Valve Chamber - Spare parts, thread tape and spanner available')}
            {renderComplianceRadio('sprinkler_valves_labelled', 'Sprinkler Valves are labelled')}
            {renderComplianceRadio('sprinkler_valve_chamber_schematic', 'Sprinkler Valve Chamber schematic chart available')}
          </div>
        )
      });
    }

    // Emergency Preparedness
    if (assessmentRequirements.emergency_preparedness) {
      sections.push({
        type: 'collapsible',
        key: 'emergency_preparedness',
        title: 'Emergency Preparedness',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('emergency_assembly_points', 'Emergency Assembly Points')}
            {renderComplianceRadio('emergency_contact_list', 'Emergency Contact List')}
            {renderComplianceRadio('emergency_procedures', 'Emergency Procedures')}
            {renderComplianceRadio('first_aid_kits', 'First Aid Kits')}
            {renderComplianceRadio('emergency_communication', 'Emergency Communication')}
            {renderComplianceRadio('emergency_drills', 'Emergency Drills')}
            {renderComplianceRadio('emergency_exits', 'Emergency Exits')}
            {renderComplianceRadio('emergency_wardens', 'Emergency Wardens')}
            {renderComplianceRadio('backup_power', 'Backup Power')}
          </div>
        )
      });
    }

    // Electrical DB
    if (assessmentRequirements.electrical_db) {
      sections.push({
        type: 'collapsible',
        key: 'electrical_safety',
        title: 'Electrical DB',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('electrical_installations', 'Electrical Installations')}
            {renderComplianceRadio('electrical_testing', 'Electrical Testing')}
            {renderComplianceRadio('electrical_safety_switches', 'Electrical Safety Switches')}
          </div>
        )
      });
    }

    // Hazardous Chemicals
    if (assessmentRequirements.hazardous_chemicals) {
      sections.push({
        type: 'collapsible',
        key: 'hazardous_materials',
        title: 'Hazardous Chemical Agents (HCA)',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('chemical_storage', 'Chemical Storage')}
            {renderComplianceRadio('chemical_handling_procedures', 'Chemical Handling Procedures')}
          </div>
        )
      });
    }

    // Stacking of Articles
    if (assessmentRequirements.stacking_articles) {
      sections.push({
        type: 'collapsible',
        key: 'stacking_articles',
        title: 'Stacking of Articles',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('stacking_height_limits', 'Stacking Height Limits')}
          </div>
        )
      });
    }

    // Housekeeping
    if (assessmentRequirements.housekeeping) {
      sections.push({
        type: 'collapsible',
        key: 'housekeeping',
        title: 'Housekeeping',
        content: (
          <div className="space-y-4">
            {renderComplianceRadio('housekeeping', 'Housekeeping')}
          </div>
        )
      });
    }

    // General Condition
     if (assessmentRequirements.general_condition) {
       sections.push({
         type: 'collapsible',
         key: 'general_condition',
         title: 'General Condition',
         content: (
           <div className="space-y-4">
             {renderComplianceRadio('floors', 'Floors')}
             {renderComplianceRadio('walls', 'Walls')}
             {renderComplianceRadio('ceilings', 'Ceilings')}
             {renderComplianceRadio('windows', 'Windows')}
             {renderComplianceRadio('doors', 'Doors')}
             {renderComplianceRadio('stairways', 'Stairways')}
             {renderComplianceRadio('lighting', 'Lighting')}
           </div>
         )
       });
     }

     // Signage Displayed
     if (assessmentRequirements.signage_displayed) {
       sections.push({
         type: 'collapsible',
         key: 'signage_displayed',
         title: 'Signage Displayed',
         content: (
           <div className="space-y-4">
             {renderComplianceRadio('signage_displayed', 'Signage displayed')}
           </div>
         )
       });
     }

     // Fire Detection Systems - now standalone since it's separate from Emergency Preparedness in some contexts
     if (assessmentRequirements.fire_detection_systems) {
       sections.push({
         type: 'collapsible',
         key: 'fire_detection_systems',
         title: 'Fire detection and alarm systems',
         content: (
           <div className="space-y-4">
             {renderComplianceRadio('fire_detection_serviced_tested_standalone', 'Fire detection and alarm systems serviced and tested, no faults on panels')}
             {renderComplianceRadio('fire_detection_operating_procedures_standalone', 'Fire detection and alarm systems - operating procedures available at the control panel')}
             {renderComplianceRadio('fire_alarm_call_points_signage_standalone', 'Fire alarm call points clearly identifiable with signage in accordance with SANS 1186-5')}
             {renderComplianceRadio('dust_covers_removed_standalone', 'Dust covers removed from smoke detectors')}
           </div>
         )
       });
     }

     // Evacuation Routes & Exits - standalone section
     if (assessmentRequirements.evacuation_routes) {
       sections.push({
         type: 'collapsible',
         key: 'evacuation_routes',
         title: 'Evacuation Routes & Exits',
         content: (
           <div className="space-y-4">
             {renderComplianceRadio('escape_doors_fire_exit_signage', 'Escape doors on the outside have "Fire Exit Keep Clear" signage displayed')}
             {renderComplianceRadio('fire_escape_plans_displayed', 'Fire Escape Plans displayed in the workplace in accordance with SANS 23601')}
           </div>
         )
       });
     }

     return sections;
  };

  const renderStep2 = () => {
    const dynamicSections = getSectionMapping();

    return (
      <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
        <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-3 sm:mb-4">Assessment Information</h3>

        {/* Show message if no sections are selected */}
        {dynamicSections.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <ClipboardCheck className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
            </div>
            <p className="text-gray-500 text-base sm:text-lg font-medium">No assessment areas selected.</p>
            <p className="text-gray-400 text-sm sm:text-base mt-2 max-w-md mx-auto">Please go back to Step 1 and select the areas you want to assess.</p>
          </div>
        )}

        {/* Render dynamic sections based on checkbox selections */}
        {dynamicSections.map((section, index) => (
          <div key={section.key} className="space-y-4">
            {section.type === 'static' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-blue-600" />
                  {section.title}
                </h4>
                {section.content}
              </div>
            ) : (
              renderCollapsibleSection(section.key, section.title, section.content)
            )}
          </div>
        ))}

        {/* Image Upload Section - always show if there are sections to assess */}
        {dynamicSections.length > 0 && (
          <div className="border-t border-gray-200 pt-4 sm:pt-6">
            {renderImageUpload()}
          </div>
        )}
      </div>
    );
  };

  const renderStepNavigation = () => (
    <div className="flex flex-col gap-4 pt-4 sm:pt-6 border-t border-gray-200">
      {/* Mobile-first layout with better spacing */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className={`px-6 py-4 sm:py-3 rounded-lg font-medium min-h-[48px] sm:min-h-[44px] w-full sm:w-auto text-base sm:text-sm transition-colors ${
            currentStep === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-500 text-white hover:bg-gray-600 active:bg-gray-700'
          }`}
        >
          ← BACK
        </button>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
          <button
            onClick={() => {
              setActiveView('list');
              resetForm();
            }}
            className="px-6 py-4 sm:py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 min-h-[48px] sm:min-h-[44px] w-full sm:w-auto order-2 sm:order-1 text-base sm:text-sm font-medium transition-colors"
          >
            Cancel
          </button>

          {currentStep < 2 ? (
            <button
              onClick={nextStep}
              disabled={!validateCurrentStep()}
              className={`px-6 py-4 sm:py-3 rounded-lg font-medium min-h-[48px] sm:min-h-[44px] w-full sm:w-auto order-1 sm:order-2 text-base sm:text-sm transition-colors ${
                validateCurrentStep()
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-md'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              NEXT →
            </button>
          ) : (
            <button
              onClick={submitAssessment}
              disabled={isLoading || !validateCurrentStep()}
              className={`px-6 py-4 sm:py-3 rounded-lg font-medium min-h-[48px] sm:min-h-[44px] w-full sm:w-auto order-1 sm:order-2 text-base sm:text-sm transition-colors ${
                validateCurrentStep() && !isLoading
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-md'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'FINISH'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex-1">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            <ClipboardCheck className="mr-2 h-5 w-5 text-blue-500 flex-shrink-0" />
            <span className="truncate">{selectedAssessment ? 'Edit HSE Assessment' : 'New HSE Assessment'}</span>
          </h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Complete the tenant safety assessment form</p>
        </div>
        <button
          onClick={() => {
            setActiveView('list');
            resetForm();
          }}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px] flex items-center justify-center w-full sm:w-auto"
        >
          ← Back to List
        </button>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Step Content */}
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}

      {/* Step Navigation */}
       {renderStepNavigation()}
    </div>
  );
};

export default HSEAssessmentForm;
